import React from 'react';

type UseColorPickerBaseProps = {
  color?: string;
  onAddPresetColor?: (color: string) => void;
};

type UseColorPickerClearableProps = UseColorPickerBaseProps & {
  clearable: true;
  onChange?: (color: string | undefined) => void;
};

type UseColorPickerStandardProps = UseColorPickerBaseProps & {
  clearable?: false;
  onChange?: (color: string) => void;
};

type UseColorPickerProps = UseColorPickerStandardProps | UseColorPickerClearableProps;

export function useColorPicker({
  color,
  onAddPresetColor,
  onChange,
  clearable,
}: UseColorPickerProps) {
  const originalColorRef = React.useRef(color);
  const currentColorRef = React.useRef(color);

  const handleExpand = React.useCallback(() => {
    originalColorRef.current = color;
  }, [color]);

  const handleCollapse = React.useCallback(() => {
    if (currentColorRef.current && currentColorRef.current !== originalColorRef.current) {
      onAddPresetColor?.(currentColorRef.current);
    }
  }, [onAddPresetColor]);

  const restoreColor = React.useCallback(() => {
    if (clearable) {
      onChange?.(originalColorRef.current);
    } else {
      if (!originalColorRef.current) {
        return;
      }

      currentColorRef.current = originalColorRef.current;

      onChange?.(originalColorRef.current);
    }
  }, [clearable, onChange]);

  const handleOnChange = React.useCallback(
    (newColor: string) => {
      currentColorRef.current = newColor;
      onChange?.(newColor);
    },
    [onChange],
  );

  return { handleExpand, handleCollapse, restoreColor, handleOnChange };
}
